import StateObserver, { user, now } from 'src/StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import { getWeeklyLeaderboardEventToClaim } from './weeklyLeaderboard.utils';
import {
  getOngoingWeeklyLeaderboardEvent,
  isWeeklyLeaderboardsAvailable,
} from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.getters';
import type { WeeklyLeaderboardOriginFeature } from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.analytics.schema';

/**
 * Open the weekly leaderboard popup
 */
export async function openWeeklyLeaderboard(
  originFeature: WeeklyLeaderboardOriginFeature,
) {
  const origin = mapFeatureToDialogOrigin(originFeature);

  // Present the popup in a promise
  const promise = openPopupPromise('popupWeeklyLeaderboard', {
    origin,
    originFeature,
  });

  // Show the info popup if the user has never seen the weekly leaderboard yet
  if (!user().weeklyLeaderboard?.lastSeenAt) {
    await openPopupPromise('popupWeeklyLeaderboardInfo', { origin });
  }

  // Update the last seen timestamp
  await StateObserver.invoke.updateWeeklyLeaderboardLastSeen();

  // Return the promise
  return promise;
}

/**
 * Claim all pending weekly leaderboard rewards, if any
 */
export async function claimPastWeeklyleaderboards() {
  const result = await withLoading(
    () => StateObserver.invoke.asyncClaimWeeklyLeaderboards(),
    {
      feature: Feature.WEEKLY_LEADERBOARD._,
      subFeature: Feature.WEEKLY_LEADERBOARD.CLAIM_REWARDS,
    },
  );
  if (result && result.rewards.length > 0) {
    const chestTypeByTier = ['gold', 'silver', 'wood'] as const;
    await openPopupPromise('popupChestRewards', {
      chestType: chestTypeByTier[result.tier],
      rewards: result.rewards,
      feature: Feature.WEEKLY_LEADERBOARD._,
      subFeature: Feature.WEEKLY_LEADERBOARD.POPUP_CHEST,
    });
  }
}

/**
 * Post puzzle sequence to show the weekly leaderboard if necessary
 * @param hasWon
 * @returns
 */
export async function weeklyLeaderboardPostGame(hasWon: boolean) {
  // Do nothing if the weekly leaderboards are not available
  if (!isWeeklyLeaderboardsAvailable(user(), now())) return;

  // Do nothing if the user has not won
  if (!hasWon) return;

  // If user has never seen the weekly leaderboard, show it
  if (!user().weeklyLeaderboard?.lastSeenAt) {
    await openWeeklyLeaderboard('postLevel');
    return;
  }

  // If there are events to claim, show the weekly leaderboard
  if (getWeeklyLeaderboardEventToClaim()) {
    await openWeeklyLeaderboard('postLevel');
    return;
  }

  // Check if there is an ongoing event
  const event = getOngoingWeeklyLeaderboardEvent(user(), now());
  if (!event) return;

  // Open the post level leaderboard if the user had not had matchmaking yet
  // This should happen when user score first time in every event.
  const entry = user().weeklyLeaderboard?.entries[event.id];
  if (entry && !entry.opponents.length) {
    await openWeeklyLeaderboard('postLevel');
  }
}

/**
 * Open the guest weekly leaderboard popup
 * It just leaderboard imitation with LINE login button for guest users.
 */
export async function openGuestWeeklyLeaderboard(
  originFeature: WeeklyLeaderboardOriginFeature,
) {
  const origin = mapFeatureToDialogOrigin(originFeature);

  // Present the popup in a promise
  return openPopupPromise('popupGuestWeeklyLeaderboard', {
    origin,
    originFeature,
  });
}
